/* eslint-disable react-refresh/only-export-components */
import { useRef, type PointerEvent } from 'react';
import { Move } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BannerCoverRender } from '@/components/theme/BannerLayerRender';
import { BANNER_H, BANNER_W, type BannerDesign } from '../model/bannerLayers';

/**
 * Live context previews above the banner canvas — every real frame the banner
 * appears in, rendered from the same layer data, cover-cropped exactly like
 * production. Drag inside any frame to reframe (set the focal point); all
 * surfaces share one focal, so every frame updates together.
 */

export interface BannerContext {
  id: string;
  label: string;
  /** Real surface aspect (w/h). */
  aspect: number;
  /** Preview width in px. */
  width: number;
}

export const PROFILE_BANNER_CONTEXTS: BannerContext[] = [
  { id: 'hero', label: 'Profile hero', aspect: 720 / 200, width: 264 },
  { id: 'popup', label: 'Chat popup', aspect: 430 / 178, width: 190 },
  { id: 'dm', label: 'DM rail', aspect: 380 / 160, width: 178 },
];

export const COMMUNITY_BANNER_CONTEXTS: BannerContext[] = [
  { id: 'rail', label: 'Channel rail', aspect: 260 / 64, width: 264 },
  { id: 'cv', label: 'Community header', aspect: 16 / 9, width: 150 },
  { id: 'explore', label: 'Explore card', aspect: 3 / 4, width: 78 },
];

export function BannerContextStrip({
  design,
  focal,
  onFocal,
  contexts,
}: {
  design: BannerDesign | null;
  focal: { x: number; y: number };
  onFocal: (focal: { x: number; y: number }) => void;
  contexts: BannerContext[];
}) {
  const hasLayers = Boolean(design && design.layers.length > 0);
  return (
    <div className="flex flex-none items-end gap-4 overflow-x-auto border-b border-white/[0.06] bg-ink-900 px-4 py-3">
      {contexts.map((ctx) => (
        <div key={ctx.id} className="flex-none">
          <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.6px] text-white/35">
            {ctx.label}
            {hasLayers && <Move size={9} className="opacity-60" />}
          </div>
          <FocalFrame ctx={ctx} design={hasLayers ? design : null} focal={focal} onFocal={onFocal} />
        </div>
      ))}
      <p className="mb-1 ml-auto hidden max-w-[180px] flex-none text-right text-[10.5px] leading-relaxed text-white/25 xl:block">
        Drag inside a frame to reframe your banner — every surface follows.
      </p>
    </div>
  );
}

function FocalFrame({
  ctx,
  design,
  focal,
  onFocal,
}: {
  ctx: BannerContext;
  design: BannerDesign | null;
  focal: { x: number; y: number };
  onFocal: (focal: { x: number; y: number }) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startX: number; startY: number; focal: { x: number; y: number } } | null>(null);

  function onDown(e: PointerEvent): void {
    if (!design) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startY: e.clientY, focal };
  }

  function onMove(e: PointerEvent): void {
    const d = drag.current;
    const el = ref.current;
    if (!d || !el || !design) return;
    const frameW = el.clientWidth;
    const frameH = el.clientHeight;
    const designAspect = BANNER_W / BANNER_H;
    const frameAspect = frameW / frameH;
    // Cover overflow in px: only one axis can pan.
    if (frameAspect < designAspect) {
      const overflowX = frameH * designAspect - frameW;
      if (overflowX <= 0) return;
      const x = clamp(d.focal.x - ((e.clientX - d.startX) / overflowX) * 100);
      onFocal({ x, y: d.focal.y });
    } else {
      const overflowY = frameW / designAspect - frameH;
      if (overflowY <= 0) return;
      const y = clamp(d.focal.y - ((e.clientY - d.startY) / overflowY) * 100);
      onFocal({ x: d.focal.x, y });
    }
  }

  return (
    <div
      ref={ref}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={() => (drag.current = null)}
      onPointerCancel={() => (drag.current = null)}
      className={cn(
        'relative touch-none select-none overflow-hidden rounded-lg border border-white/[0.1] bg-ink-950 shadow-[0_10px_28px_rgba(0,0,0,0.4)]',
        design && 'cursor-grab active:cursor-grabbing',
      )}
      style={{ width: ctx.width, aspectRatio: `${ctx.aspect}` }}
    >
      {design ? (
        <BannerCoverRender design={design} focal={focal} />
      ) : (
        <div className="grid h-full w-full place-items-center text-[9px] font-bold text-white/20">
          No layers yet
        </div>
      )}
    </div>
  );
}

function clamp(v: number): number {
  return Math.min(100, Math.max(0, v));
}
